<script setup>
// Pick five seconds of a video. That is the whole job of this sheet.
//
// The scrubber sets where the window STARTS; the window is always five seconds
// (or the whole clip, if it is shorter). Twelve frames come out of that window,
// evenly spaced, at grid width — and those frames are what gets stored. The
// video itself never leaves this sheet.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { IonModal } from '@ionic/vue';
import { say } from '../fax/voice.js';
import { framesFromVideo, videoDuration, CLIP_SECONDS, CLIP_FRAMES } from '../fax/frames.js';
import { GRID_W } from '../fax/geometry.js';

const props = defineProps({ file: { type: File, required: true } });
const emit = defineEmits(['done', 'close']);
const open = ref(true);
let pending = null;            // what to emit once Ionic has finished dismissing
const close = () => { open.value = false; };
function afterDismiss() { const p = pending; pending = null; if (p) emit('done', p); else emit('close'); }

const url = URL.createObjectURL(props.file);
const duration = ref(0);
const start = ref(0);
const busy = ref(false);
const error = ref('');
const videoEl = ref(null);

const span = computed(() => Math.min(CLIP_SECONDS, Math.max(0.1, duration.value - start.value)));
const maxStart = computed(() => Math.max(0, duration.value - CLIP_SECONDS));
const mmss = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
const windowLabel = computed(() => `${mmss(start.value)} – ${mmss(start.value + span.value)}`);

onMounted(async () => {
  duration.value = await videoDuration(props.file);
  if (!duration.value) error.value = say('THIS MACHINE CANNOT READ THAT VIDEO.', "This machine can't read that video.");
});
onBeforeUnmount(() => URL.revokeObjectURL(url));

// The video follows the scrubber, so what you see is the frame the window
// starts on — not a thumbnail of the whole file.
function scrub() {
  const v = videoEl.value;
  if (v && duration.value) v.currentTime = Math.min(start.value, Math.max(0, duration.value - 0.05));
}
// iOS paints nothing until it is asked for a time. 0.01 rather than 0: a seek
// to exactly 0 on a fresh element is a no-op there and the box stays black.
function firstFrame() {
  const v = videoEl.value;
  if (v) { try { v.currentTime = 0.01; } catch { /* not ready */ } }
}

async function use() {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    const { bitmaps, delaysMs } = await framesFromVideo(props.file, {
      startSec: start.value, seconds: CLIP_SECONDS, max: CLIP_FRAMES,
    });
    // Down to grid width here, once, so the stored recipe is small and the
    // render never has to resample a 4K frame.
    const c = document.createElement('canvas');
    const frames = bitmaps.map((b) => {
      const scale = Math.min(1, GRID_W / b.width);
      c.width = Math.round(b.width * scale); c.height = Math.round(b.height * scale);
      const g = c.getContext('2d');
      g.drawImage(b, 0, 0, c.width, c.height);
      b.close?.();
      return c.toDataURL('image/jpeg', 0.86);
    });
    c.width = c.height = 0;
    pending = { frames, delaysMs };
    close();
  } catch (e) {
    error.value = e?.code === 'cannot-play'
      ? say('THIS MACHINE CANNOT READ THAT VIDEO. TRY AN MP4.', "This machine can't read that video. Try an MP4.")
      : say('THE MACHINE JAMMED. TRY AGAIN.', 'The machine jammed. Try again.');
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <IonModal :is-open="open" :breakpoints="[0, 0.94]" :initial-breakpoint="0.94" :handle="false" class="trim-sheet" :aria-label="say('PICK FIVE SECONDS', 'Pick five seconds')" @didDismiss="afterDismiss">
    <div class="modal-panel">
      <div class="head">
        <span class="silkscreen">{{ say('PICK FIVE SECONDS', 'Pick five seconds') }}</span>
        <button type="button" class="closer" @click="close">{{ say('CLOSE', 'Close') }}</button>
      </div>

      <video ref="videoEl" class="tape" :src="url" muted playsinline preload="auto" @loadedmetadata="firstFrame" />

      <label class="scrub">
        <span class="silkscreen">{{ say('STARTS AT', 'Starts at') }} <span class="num">{{ windowLabel }}</span></span>
        <input
          v-model.number="start"
          type="range" min="0" :max="maxStart" step="0.1"
          :disabled="!duration || busy"
          :aria-valuetext="windowLabel"
          @input="scrub"
          @touchmove.stop
          @pointerdown.stop
        >
      </label>

      <p v-if="error" class="field-error">{{ error }}</p>

      <button type="button" class="press" :disabled="!duration || busy" @click="use">
        {{ busy ? say('READING THE TAPE…', 'Reading the tape…') : say('USE THESE FIVE SECONDS', 'Use these five seconds') }}
      </button>
      <p class="silkscreen">{{ say('FORTY FRAMES. NEVER LEAVES THIS DEVICE.', 'Forty frames. Never leaves this device.') }}</p>
    </div>
  </IonModal>
</template>

<style scoped>
/* NOT .panel: that is the machine's control panel in app.css, and its
   flex-wrap: wrap leaked in here. A wrapping column with the keyboard up put
   the whole GIF grid in a second column off the right edge of the phone. */
.modal-panel {
  flex-wrap: nowrap; align-items: stretch;
  width: 100%; height: 100%; max-width: none; max-height: none;
  display: flex; flex-direction: column; gap: 10px;
  padding: 12px 14px calc(14px + env(safe-area-inset-bottom));
  background: var(--machine);
}
.head { display: flex; align-items: center; justify-content: space-between; }
.tape { width: 100%; max-height: 44vh; background: #000; border-radius: 6px; }
.scrub { display: flex; flex-direction: column; gap: 6px; }
.scrub input[type=range] { width: 100%; min-height: 52px; touch-action: pan-x; -webkit-appearance: none; background: transparent; }
/* iOS's default thumb is 28px and easy to miss with chalk on your hands. */
.scrub input[type=range]::-webkit-slider-runnable-track { height: 8px; border-radius: 4px; background: var(--machine-dark); }
.scrub input[type=range]::-webkit-slider-thumb {
  -webkit-appearance: none; width: 44px; height: 44px; margin-top: -18px; border-radius: 50%;
  background: var(--led-red); border: 3px solid var(--machine); box-shadow: 0 1px 3px rgba(0,0,0,.4);
}
/* THE PRIMARY BUTTON HAS TO LOOK PRESSABLE. It was class="press" on the
   assumption that the tray lip's recipe was global; it is scoped to
   .tray-in, so this was a grey system button under a red slider thumb and a
   styled CLOSE — the one thing on the sheet that looked disabled. */
.press {
  display: flex; align-items: center; justify-content: center; width: 100%;
  min-height: 52px; padding: var(--sp-2) var(--sp-3); border: 0;
  border-radius: var(--radius-sm); background: var(--led-red);
  box-shadow: inset 0 -2px 0 rgba(0, 0, 0, 0.25); color: var(--accent-contrast);
  font-family: var(--mono); font-weight: 900; font-size: var(--text-md);
  letter-spacing: var(--tracking-wide); text-transform: uppercase; cursor: pointer;
}
.press:disabled { background: var(--machine-dark); color: var(--machine-light); box-shadow: none; cursor: default; }
/* THE SHEET IS AN IonModal, NOT A position: fixed PANEL. An Ionic page uses
   transform and contain, which makes `fixed` mean "relative to the page" — so
   when the keyboard rose on a phone the panel was shoved offscreen and the
   grid collapsed to a single column at the right edge. Ionic presents a modal
   at the app root and handles the keyboard itself. */
:global(ion-modal.trim-sheet) { --background: var(--machine); --border-radius: 14px 14px 0 0; }
/* .dial is the CONDITION dial's container; on a lone button it styled nothing. */
.closer {
  min-height: var(--tap); padding: 0 14px;
  border: 2px solid var(--machine-dark); border-radius: var(--radius-sm);
  background: var(--machine-light); color: var(--legend);
  font-family: var(--mono); font-size: var(--text-xs); font-weight: 800;
  letter-spacing: var(--tracking-wide); text-transform: uppercase; cursor: pointer;
}
</style>